import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import baseURL from '../../Components/url';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileContract,
    faShieldAlt,
    faUserShield,
    faChild,
    faUndo,
    faPrint,
    faSync,
    faBuilding,
    faPhone,
    faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import './PoliticasPublica.css';

export default function PoliticasPublica() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { idPolitica: paramId } = useParams();

    const [politicas, setPoliticas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [politicaSeleccionada, setPoliticaSeleccionada] = useState(null);
    const [contacto, setContacto] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        cargarPoliticas();
        cargarContacto();
    }, []);

    const cargarContacto = () => {
        fetch(`${baseURL}/contactoGet.php`, {
            method: 'GET',
        })
            .then(res => res.json())
            .then(data => {
                const lista = data.contacto || [];
                if (lista.length > 0) {
                    setContacto(lista[0]);
                }
            })
            .catch(err => console.error('Error al cargar datos de contacto:', err));
    };

    const cargarPoliticas = () => {
        setLoading(true);
        fetch(`${baseURL}/politicasGet.php`, {
            method: 'GET',
        })
            .then(res => res.json())
            .then(data => {
                const lista = data.politicas || [];
                // Filtrar activas
                const activas = lista.filter(p => p.estado !== 'inactivo');
                setPoliticas(activas.length > 0 ? activas : lista);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error al cargar políticas:', err);
                setLoading(false);
            });
    };

    // Determinar qué política está activa según ?id= o :idPolitica
    useEffect(() => {
        if (politicas.length === 0) return;

        const idBuscado = paramId || searchParams.get('id');

        if (idBuscado) {
            const encontrada = politicas.find(p => String(p.idPolitica) === String(idBuscado));
            if (encontrada) {
                setPoliticaSeleccionada(encontrada);
                return;
            }
        }

        // Si no hay id o no se encuentra, seleccionar la primera
        setPoliticaSeleccionada(politicas[0]);
    }, [politicas, searchParams, paramId]);

    const seleccionarPolitica = (p) => {
        setPoliticaSeleccionada(p);
        setSearchParams({ id: p.idPolitica });
        window.scrollTo({ top: 180, behavior: 'smooth' });
    };

    // Función para obtener nombre abreviado para las pestañas
    const obtenerNombreAbreviado = (p) => {
        const tit = (p.titulo || '').toUpperCase();
        if (tit.includes('TÉRMINOS') || tit.includes('TERMINOS')) return 'Términos y Condiciones';
        if (tit.includes('DATOS') || tit.includes('PRIVACIDAD')) return 'Protección de Datos';
        if (tit.includes('EXPLOTACIÓN') || tit.includes('MENOR') || tit.includes('NIÑOS')) return 'Protección al Menor';
        if (tit.includes('CANCELACIÓN') || tit.includes('COBRO') || tit.includes('REEMBOLSO')) return 'Cancelación y Reembolso';
        if (p.categoria && p.categoria !== 'General') return p.categoria;
        return p.titulo.replace(/^[I|V|X]+\.?\s*[-–—]?\s*/i, '').slice(0, 26);
    };

    const obtenerIcono = (p) => {
        const tit = (p.titulo || '').toUpperCase();
        if (tit.includes('DATOS') || tit.includes('PRIVACIDAD')) return faUserShield;
        if (tit.includes('MENOR') || tit.includes('NIÑOS')) return faChild;
        if (tit.includes('CANCELACIÓN') || tit.includes('REEMBOLSO')) return faUndo;
        return faFileContract;
    };

    return (
        <div className="politicas-public-page">
            <div className="politicas-public-wrapper">
                {/* Banner Superior */}
                <div className="politicas-hero-banner">
                    <h1>Términos, Condiciones y Políticas</h1>
                    <p>
                        Conoce las disposiciones legales, protección de datos y términos de servicio aplicables en nuestras operaciones y canales digitales.
                    </p>
                </div>

                {loading ? (
                    <div className="politica-loading">
                        <FontAwesomeIcon icon={faSync} spin size="2x" style={{ color: 'var(--color1, #3F1D76)' }} />
                        <p style={{ marginTop: '12px' }}>Cargando políticas y condiciones...</p>
                    </div>
                ) : politicas.length === 0 ? (
                    <div className="politica-card-content" style={{ textAlign: 'center' }}>
                        <h3>Información en actualización</h3>
                        <p>Las políticas y términos se encuentran actualmente en proceso de actualización.</p>
                    </div>
                ) : (
                    <>
                        {/* Pestañas de Navegación Rápida */}
                        <div className="politicas-nav-tabs">
                            {politicas.map((p) => {
                                const isSelected = politicaSeleccionada && politicaSeleccionada.idPolitica === p.idPolitica;
                                return (
                                    <button
                                        key={p.idPolitica}
                                        type="button"
                                        className={`politica-tab-btn ${isSelected ? 'active' : ''}`}
                                        onClick={() => seleccionarPolitica(p)}
                                    >
                                        <FontAwesomeIcon icon={obtenerIcono(p)} />
                                        <span>{obtenerNombreAbreviado(p)}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Contenido de la Política Seleccionada */}
                        {politicaSeleccionada && (
                            <div className="politica-card-content">
                                <div className="politica-card-header">
                                    <div className="politica-meta-row">
                                        <span className="politica-tag">
                                            {politicaSeleccionada.categoria || 'Legal'}
                                        </span>

                                    </div>

                                    {/* Imagen adjunta / afiche institucional (encima del título) */}
                                    {politicaSeleccionada.imagen && (
                                        <div className="politica-destacada-img-wrapper">
                                            <img
                                                src={politicaSeleccionada.imagen}
                                                alt={politicaSeleccionada.titulo}
                                                className="politica-destacada-img"
                                            />
                                        </div>
                                    )}

                                    <h2>{politicaSeleccionada.titulo}</h2>
                                </div>

                                <div className="politica-text-body">
                                    {politicaSeleccionada.descripcion}
                                </div>

                                {/* Cuadro final informativo de la empresa */}
                                <div className="politica-company-footer-box">
                                    <div className="company-box-info">
                                        <strong>
                                            {contacto?.nombre || 'Seylis S.A.C. — Tingo Aventura Tours'}
                                        </strong>
                                        {(contacto?.ruc || contacto?.representante) ? (
                                            <span>
                                                {contacto?.ruc ? (contacto.ruc.toLowerCase().includes('ruc') ? contacto.ruc : `RUC: ${contacto.ruc}`) : ''}
                                                {contacto?.ruc && contacto?.representante ? ' | ' : ''}
                                                {contacto?.representante ? (contacto.representante.toLowerCase().includes('representante') ? contacto.representante : `Representante: ${contacto.representante}`) : ''}
                                            </span>
                                        ) : (
                                            <span>RUC: 20611934484 | Representante: Balladares Burga Segundo Encarnación</span>
                                        )}
                                        {(contacto?.email || contacto?.telefono) ? (
                                            <span>
                                                Consultas legales: {contacto?.email || 'info@tingoaventuratours.com'}
                                                {contacto?.telefono ? ` | ${contacto.telefono}` : ''}
                                            </span>
                                        ) : (
                                            <span>Consultas legales: info@tingoaventuratours.com | +51 933 936 032</span>
                                        )}
                                        {contacto?.direccion && (
                                            <span>
                                                Dirección: {contacto.direccion}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
